import React, { useState, useEffect } from 'react';
import { RiCheckLine } from '@remixicon/react';
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle
} from '../../../../components/ui/Drawer';
import { formatPrice } from '@/utils';

/**
 * EditPricingDrawer Component
 * Sidebar drawer for editing aircraft pricing
 *
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether drawer is open
 * @param {Function} props.onClose - Callback to close drawer
 * @param {Object} props.aircraft - Aircraft data to edit
 * @param {number} props.passengerCount - Number of passengers for segment tax calculation
 * @param {string} props.flightType - Type of flight: 'one-way', 'round-trip', or 'multi-leg'
 * @param {number} props.flightsLength - Number of flights (legs) for multi-leg trips
 * @param {Function} props.onSave - Callback to save pricing changes
 */
const EditPricingDrawer = ({
  isOpen = false,
  onClose,
  aircraft,
  passengerCount = 6,
  flightType = 'one-way',
  flightsLength = 1,
  onSave,
}) => {
  // Tax rate constants
  const BROKER_MARGIN_RATE = 0.15; // 15% Brokerage Margin
  const FET_RATE = 0.075; // 7.5% Federal Excise Tax
  const PLATFORM_FEE_RATE = 0.015; // 1.5% TrustJet Fee
  const CREDIT_CARD_FEE_RATE = 0.04; // 4% Credit Card Fee

  // Calculate leg multiplier based on flight type
  const getLegMultiplier = () => {
    if (flightType === 'round-trip') return 2;
    if (flightType === 'multi-leg') return flightsLength;
    return 1; // one-way
  };
  const legMultiplier = getLegMultiplier();

  // Get segment fee label based on flight type
  const getSegmentFeeLabel = () => {
    if (flightType === 'round-trip') return '(Round trip)';
    if (flightType === 'multi-leg') return `(${flightsLength} legs)`;
    return '(One-way)';
  };

  // Local state for editable fields
  const [operatorCost, setOperatorCost] = useState('');
  const [brokerMargin, setBrokerMargin] = useState('');
  const [isDomestic, setIsDomestic] = useState(true);
  const [isInternational, setIsInternational] = useState(false);
  const [federalExciseTax, setFederalExciseTax] = useState('');
  const [paxCount, setPaxCount] = useState('6');
  const [feePerLeg, setFeePerLeg] = useState('5.20');

  // Round to 2 decimal places
  const round2 = (num) => Math.round(num * 100) / 100;

  // Initialize values when aircraft changes
  useEffect(() => {
    if (aircraft && isOpen) {
      const opCost = parseFloat(aircraft.total_price) || 0;
      // Use saved broker_markup if available, otherwise calculate default
      const brokerMkup = parseFloat(aircraft.broker_markup) || round2(opCost * BROKER_MARGIN_RATE);

      setOperatorCost(opCost.toString());
      setBrokerMargin(brokerMkup.toString());

      // Use saved federal_excise_tax if available, otherwise calculate
      const savedFet = parseFloat(aircraft.federal_excise_tax);
      if (!isNaN(savedFet) && savedFet > 0) {
        setFederalExciseTax(savedFet.toString());
      } else {
        const taxableBase = opCost + brokerMkup;
        const fet = round2(taxableBase * FET_RATE);
        setFederalExciseTax(fet.toString());
      }

      // Use saved pax count if available, otherwise use passengerCount prop
      const savedPaxCount = aircraft.segment_pax_count;
      if (savedPaxCount !== undefined && savedPaxCount !== null) {
        setPaxCount(savedPaxCount.toString());
      } else {
        setPaxCount(passengerCount.toString());
      }

      // Use saved fee per leg if available, otherwise default
      const savedFeePerLeg = parseFloat(aircraft.segment_fee_per_leg);
      if (!isNaN(savedFeePerLeg) && savedFeePerLeg > 0) {
        setFeePerLeg(savedFeePerLeg.toString());
      } else {
        setFeePerLeg('5.20');
      }
    }
  }, [aircraft, isOpen, passengerCount, legMultiplier]);

  // Calculate derived values
  const opCostNum = parseFloat(operatorCost) || 0;
  const brokerMarginNum = parseFloat(brokerMargin) || 0;
  const subtotalCost = round2(opCostNum + brokerMarginNum);
  const trustJetFee = round2(subtotalCost * PLATFORM_FEE_RATE);
  const fetNum = parseFloat(federalExciseTax) || 0;
  const paxNum = parseInt(paxCount) || 0;
  const feePerLegNum = parseFloat(feePerLeg) || 0;
  const totalSegmentFee = round2(paxNum * feePerLegNum * legMultiplier);

  // Calculate credit card fee (4% of total cost before credit card fee)
  const totalBeforeCCFee = round2(subtotalCost + trustJetFee + fetNum + totalSegmentFee);
  const creditCardFee = round2(totalBeforeCCFee * CREDIT_CARD_FEE_RATE);

  // Handle save
  const handleSave = () => {
    if (onSave && aircraft) {
      const optionId = aircraft.bid_option_id || aircraft.aircraft_id;

      // Calculate total cost (excluding credit card fee)
      const totalCost = totalBeforeCCFee;

      onSave(optionId, {
        total_price: operatorCost,
        broker_markup: brokerMargin,
        federal_excise_tax: federalExciseTax,
        platform_fee: trustJetFee.toFixed(2),
        segment_tax: totalSegmentFee.toFixed(2),
        segment_pax_count: paxCount,
        segment_fee_per_leg: feePerLeg,
        credit_card_fee: creditCardFee.toFixed(2),
        final_price: totalCost.toFixed(2),
      });

      onClose();
    }
  };

  // Calculate margin percentage for display
  const marginPercentage = opCostNum > 0 ? round2((brokerMarginNum / opCostNum) * 100) : 15;

  return (
    <Drawer open={isOpen} onOpenChange={onClose}>
      <DrawerContent side="right" className="w-full max-w-md flex flex-col">
        <DrawerHeader className="sr-only">
          <DrawerTitle>Edit Pricing</DrawerTitle>
        </DrawerHeader>

        {/* Header */}
        <div className="px-6 pt-6 pb-4">
          <h2 className="text-2xl font-semibold text-gray-900">Edit Pricing</h2>
          <p className="text-gray-500 mt-1">
            {aircraft?.aircraft?.model || aircraft?.aircraft?.make_model || 'Aircraft'}
          </p>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 pb-24">
          {/* Operator Cost & Brokerage Margin Row */}
          <div className="grid grid-cols-2 gap-4 mb-6">
            {/* Operator Cost */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Operator Cost
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                <input
                  type="text"
                  value={operatorCost}
                  onChange={(e) => {
                    const value = e.target.value.replace(/[^0-9.]/g, '');
                    setOperatorCost(value);
                    // Auto-calculate broker margin at 15%
                    const newOpCost = parseFloat(value) || 0;
                    const newBrokerMargin = round2(newOpCost * BROKER_MARGIN_RATE);
                    setBrokerMargin(newBrokerMargin.toString());
                    // Auto-calculate FET
                    const taxableBase = newOpCost + newBrokerMargin;
                    const newFet = round2(taxableBase * FET_RATE);
                    setFederalExciseTax(newFet.toString());
                  }}
                  className="w-full pl-7 pr-3 py-3 border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">
                ${opCostNum > 0 ? round2(opCostNum / 6).toLocaleString() : '0'}/hr base rate
              </p>
            </div>

            {/* Brokerage Margin */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Brokerage Margin
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                <input
                  type="text"
                  value={brokerMargin}
                  onChange={(e) => {
                    const value = e.target.value.replace(/[^0-9.]/g, '');
                    setBrokerMargin(value);
                    // Recalculate FET
                    const taxableBase = opCostNum + (parseFloat(value) || 0);
                    const newFet = round2(taxableBase * FET_RATE);
                    setFederalExciseTax(newFet.toString());
                  }}
                  className="w-full pl-7 pr-3 py-3 border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">
                {marginPercentage}% margin
              </p>
            </div>
          </div>

          {/* Subtotal Cost */}
          <div className="flex justify-between items-center py-4 border-b border-gray-200">
            <span className="text-sm text-gray-700">Subtotal Cost</span>
            <span className="text-lg font-semibold text-gray-900">
              {formatPrice(subtotalCost)}
            </span>
          </div>

          {/* TrustJet Fee */}
          <div className="flex justify-between items-center py-4 border-b border-gray-200">
            <span className="text-sm text-gray-600">TrustJet Fee (1.5%)</span>
            <span className="text-sm text-gray-900">
              {formatPrice(trustJetFee)}
            </span>
          </div>

          {/* Credit Card Fee */}
          <div className="flex justify-between items-center py-4 border-b border-gray-200">
            <span className="text-sm text-gray-600">Credit Card Fee (4%)</span>
            <span className="text-sm text-gray-900">
              {formatPrice(creditCardFee)}
            </span>
          </div>

          {/* Domestic Trip Checkbox */}
          <div className="flex justify-between items-center py-4 border-b border-gray-200">
            <span className="text-sm text-gray-700">Domestic Trip</span>
            <input
              type="checkbox"
              checked={isDomestic}
              onChange={(e) => setIsDomestic(e.target.checked)}
              className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
          </div>

          {/* International Trip Checkbox */}
          <div className="flex justify-between items-center py-4 border-b border-gray-200">
            <span className="text-sm text-gray-400">International Trip</span>
            <input
              type="checkbox"
              checked={isInternational}
              disabled={true}
              onChange={(e) => setIsInternational(e.target.checked)}
              className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
          </div>

          {/* Federal Excise Tax */}
          <div className="py-4 border-b border-gray-200">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Federal Excise Tax
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
              <input
                type="text"
                value={federalExciseTax}
                onChange={(e) => {
                  const value = e.target.value.replace(/[^0-9.]/g, '');
                  setFederalExciseTax(value);
                }}
                className="w-full pl-7 pr-3 py-3 border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <p className="text-xs text-gray-500 mt-1">
              7.5% subtotal cost
            </p>
          </div>

          {/* Segment Fee */}
          <div className="py-4">
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Segment Fee <span className="font-normal text-gray-500">{getSegmentFeeLabel()}</span>
            </label>
            <div className="flex items-center gap-2">
              {/* Pax Count */}
              <div className="w-16">
                <input
                  type="text"
                  value={paxCount}
                  onChange={(e) => {
                    const value = e.target.value.replace(/[^0-9]/g, '');
                    setPaxCount(value);
                  }}
                  readOnly
                  className="w-full pl-7 pr-3 py-3 bg-gray-50 border border-gray-300 rounded-lg text-gray-900"
                />
                <p className="text-xs text-gray-500 mt-1 text-center">Pax</p>
              </div>

              <span className="text-gray-500">x</span>

              {/* Fee per leg */}
              <div className="w-24">
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                  <input
                    type="text"
                    value={feePerLeg}
                    onChange={(e) => {
                      const value = e.target.value.replace(/[^0-9.]/g, '');
                      setFeePerLeg(value);
                    }}
                    className="w-full pl-7 pr-3 py-3 border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1 text-center">Fee per leg</p>
              </div>

              <span className="text-gray-500">=</span>

              {/* Total segment fee */}
              <div className="flex-1">
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                  <input
                    type="text"
                    value={totalSegmentFee.toFixed(2)}
                    readOnly
                    className="w-full pl-7 pr-3 py-3 bg-gray-50 border border-gray-300 rounded-lg text-gray-900"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1 text-center">Total segment fee</p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="absolute bottom-0 left-0 right-0 p-4 mb-16 bg-white border-t border-gray-200">
          <button
            type="button"
            onClick={handleSave}
            className="w-full px-4 py-3 bg-gray-900 text-white rounded-full hover:bg-gray-800 transition-colors flex items-center justify-center gap-2 font-medium"
          >
            <RiCheckLine className="w-5 h-5" />
            Save Pricing
          </button>
        </div>
      </DrawerContent>
    </Drawer>
  );
};

export default EditPricingDrawer;
